import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Form, Input, Select, Switch, Space, message, Tooltip, Button, Row, Col, Tag
} from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { TextArea } = Input;
const { Option } = Select;

/**
 * 酒店新建/编辑弹窗（左右两栏布局）
 * 酒店库管理 与 通知书工作台-酒店分配 共用，保证两处表单完全一致。
 *
 * props:
 * - open: 是否显示
 * - editingHotel: 编辑的酒店记录，null 为新建
 * - onCancel: 关闭回调
 * - onSaved: 保存成功回调，参数为后端返回的酒店对象（新建时含 id）
 */
const HotelFormModal = ({ open, editingHotel, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [translating, setTranslating] = useState({});
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchNoteTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/hotel-note-templates`, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) setNoteTemplates(res.data.data);
    } catch (err) {
      console.error('获取模板失败:', err);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchNoteTemplates();
    if (editingHotel) {
      form.setFieldsValue({
        destinationIds: editingHotel.destinationIds?.length > 0 ? editingHotel.destinationIds : (editingHotel.destinationId ? [editingHotel.destinationId] : undefined),
        noteTemplateId: editingHotel.noteTemplateId || undefined,
        name: editingHotel.name,
        nameEn: editingHotel.nameEn,
        starLevel: editingHotel.starLevel,
        city: editingHotel.city,
        address: editingHotel.address,
        addressEn: editingHotel.addressEn,
        phone: editingHotel.phone,
        transportation: editingHotel.transportation,
        transportationEn: editingHotel.transportationEn,
        notes: editingHotel.notes,
        notesEn: editingHotel.notesEn,
        checkInTime: editingHotel.checkInTime,
        checkOutTime: editingHotel.checkOutTime,
        hasBreakfast: editingHotel.hasBreakfast,
        isActive: editingHotel.isActive
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        starLevel: 4,
        checkInTime: '14:00',
        checkOutTime: '12:00',
        isActive: true
      });
    }
  }, [open, editingHotel, form, fetchNoteTemplates]);

  const handleSelectTemplate = (templateId) => {
    const tmpl = noteTemplates.find(t => t.id === templateId);
    if (tmpl) {
      form.setFieldsValue({
        noteTemplateId: templateId,
        notes: tmpl.notes,
        notesEn: tmpl.notesEn,
      });
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      if (editingTemplate) {
        await axios.put(`${API_BASE}/hotel-note-templates/${editingTemplate.id}`, values, { headers: AuthService.getAuthHeaders() });
        message.success('模板更新成功');
      } else {
        await axios.post(`${API_BASE}/hotel-note-templates`, values, { headers: AuthService.getAuthHeaders() });
        message.success('模板创建成功');
      }
      setTemplateModalVisible(false);
      fetchNoteTemplates();
    } catch (err) {
      if (err.response?.data?.error) message.error(err.response.data.error);
    }
  };

  const handleTranslate = async (chineseField, englishField) => {
    const chineseText = form.getFieldValue(chineseField);
    if (!chineseText || chineseText.trim() === '') {
      message.warning('请先填写中文内容');
      return;
    }
    setTranslating(prev => ({ ...prev, [chineseField]: true }));
    try {
      const response = await axios.post(`${API_BASE}/translate`, {
        text: chineseText,
        context: '酒店相关信息'
      }, { headers: AuthService.getAuthHeaders() });
      if (response.data.success && response.data.data.translation) {
        form.setFieldsValue({ [englishField]: response.data.data.translation });
        message.success('翻译完成');
      }
    } catch (error) {
      console.error('翻译失败:', error);
      message.error('翻译失败，请稍后重试');
    } finally {
      setTranslating(prev => ({ ...prev, [chineseField]: false }));
    }
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    try {
      setSaving(true);
      let res;
      if (editingHotel) {
        res = await axios.put(`${API_BASE}/hotels/${editingHotel.id}`, values, { headers: AuthService.getAuthHeaders() });
        message.success('更新成功');
      } else {
        res = await axios.post(`${API_BASE}/hotels`, values, { headers: AuthService.getAuthHeaders() });
        message.success('创建成功');
      }
      onSaved && onSaved(res.data?.data);
    } catch (error) {
      message.error(error.response?.data?.message || error.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const translateLabel = (text, cnField, enField) => (
    <span>
      {text}{' '}
      <Tooltip title="翻译到英文">
        <Button
          type="primary"
          size="small"
          icon={<TranslationOutlined />}
          loading={translating[cnField]}
          onClick={() => handleTranslate(cnField, enField)}
        />
      </Tooltip>
    </span>
  );

  return (
    <>
      <Modal
        title={editingHotel ? '编辑酒店' : '新建酒店'}
        open={open}
        onOk={handleSave}
        onCancel={onCancel}
        confirmLoading={saving}
        width={1000}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Row gutter={32}>
            {/* 左栏：基本信息 */}
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="酒店名称（中文）"
                rules={[{ required: true, message: '请输入酒店名称' }]}
              >
                <Input placeholder="如：布宜诺斯艾利斯希尔顿酒店" />
              </Form.Item>

              <Form.Item name="nameEn" label={translateLabel('酒店名称（English）', 'name', 'nameEn')}>
                <Input placeholder="e.g. Hilton Buenos Aires" />
              </Form.Item>

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item
                    name="starLevel"
                    label="星级"
                    rules={[{ required: true, message: '请选择星级' }]}
                  >
                    <Select>
                      <Option value={5}>5星</Option>
                      <Option value={4}>4星</Option>
                      <Option value={3}>3星</Option>
                      <Option value={2}>2星</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="city"
                    label="所在城市"
                    rules={[{ required: true, message: '请输入城市' }]}
                  >
                    <Input placeholder="如：布宜诺斯艾利斯" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="address" label="地址（中文）">
                <Input placeholder="酒店详细地址" />
              </Form.Item>

              <Form.Item name="addressEn" label={translateLabel('地址（English）', 'address', 'addressEn')}>
                <Input placeholder="e.g. Av. Macacha Güemes 351, Buenos Aires" />
              </Form.Item>

              <Row gutter={12}>
                <Col span={10}>
                  <Form.Item name="phone" label="电话">
                    <Input placeholder="如：+54 11 1234 5678" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="checkInTime" label="入住时间">
                    <Input placeholder="14:00" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="checkOutTime" label="退房时间">
                    <Input placeholder="12:00" />
                  </Form.Item>
                </Col>
              </Row>

              <Space size="large">
                <Form.Item name="hasBreakfast" label="含早餐" valuePropName="checked">
                  <Switch checkedChildren="含" unCheckedChildren="无" />
                </Form.Item>
                <Form.Item name="isActive" label="状态" valuePropName="checked">
                  <Switch checkedChildren="启用" unCheckedChildren="停用" />
                </Form.Item>
              </Space>
            </Col>

            {/* 右栏：交通与注意事项 */}
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 16, padding: '12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="end">
                  <Form.Item name="noteTemplateId" label="注意事项模板" style={{ marginBottom: 0 }}>
                    <Select
                      placeholder="选择模板自动填充注意事项"
                      allowClear
                      onChange={handleSelectTemplate}
                      style={{ width: 280 }}
                    >
                      {noteTemplates.map(t => (
                        <Option key={t.id} value={t.id}>{t.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Button size="small" onClick={() => { setEditingTemplate(null); templateForm.resetFields(); setTemplateModalVisible(true); }}>
                    管理模板
                  </Button>
                </Space>
              </div>

              <Form.Item name="transportation" label="交通信息（中文）（以实际情况为准）">
                <TextArea rows={3} placeholder="距机场距离、交通方式等..." />
              </Form.Item>

              <Form.Item name="transportationEn" label={translateLabel('交通信息（English）', 'transportation', 'transportationEn')}>
                <TextArea rows={3} placeholder="e.g. 30 minutes from airport by shuttle..." />
              </Form.Item>

              <Form.Item name="notes" label="注意事项（中文）">
                <TextArea rows={3} placeholder="入住注意事项、特殊说明等..." />
              </Form.Item>

              <Form.Item name="notesEn" label={translateLabel('注意事项（English）', 'notes', 'notesEn')}>
                <TextArea rows={3} placeholder="e.g. Check-in requires passport and credit card..." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 注意事项模板管理弹窗 */}
      <Modal
        title={editingTemplate ? '编辑模板' : '新建模板'}
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        onOk={handleSaveTemplate}
        width={700}
        okText="保存"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <strong>已有模板：</strong>
          <Space wrap style={{ marginTop: 8 }}>
            {noteTemplates.map(t => (
              <Tag
                key={t.id}
                color="blue"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setEditingTemplate(t);
                  templateForm.setFieldsValue(t);
                }}
              >
                {t.name}
              </Tag>
            ))}
            {noteTemplates.length === 0 && <span style={{ color: '#999' }}>暂无模板</span>}
          </Space>
        </div>
        <Form form={templateForm} layout="vertical">
          <Form.Item name="name" label="模板名称" rules={[{ required: true, message: '请输入模板名称' }]}>
            <Input placeholder="如：通用酒店注意事项" />
          </Form.Item>
          <Form.Item name="notes" label="注意事项（中文）">
            <TextArea rows={4} placeholder="入住注意事项..." />
          </Form.Item>
          <Form.Item name="notesEn" label={<span>注意事项（English） <Tooltip title="翻译到英文"><Button type="primary" size="small" icon={<TranslationOutlined />} loading={translating.templateNotes} onClick={async () => {
            const text = templateForm.getFieldValue('notes');
            if (!text?.trim()) { message.warning('请先填写中文注意事项'); return; }
            try {
              setTranslating(prev => ({ ...prev, templateNotes: true }));
              const res = await axios.post(`${API_BASE}/translate`, { text, targetLang: 'en' }, { headers: AuthService.getAuthHeaders() });
              if (res.data.success) { templateForm.setFieldsValue({ notesEn: res.data.data.translation }); message.success('翻译成功'); }
            } catch { message.error('翻译失败'); }
            finally { setTranslating(prev => ({ ...prev, templateNotes: false })); }
          }} /></Tooltip></span>}>
            <TextArea rows={4} placeholder="Hotel check-in notes..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default HotelFormModal;
